import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Calendar, Plus, Edit, Trash2, AlertCircle, Save, X, RefreshCw, CheckCircle2, MapPin } from 'lucide-react'
import { ImageUpload } from '@/components/admin/image-upload'
import { format, parseISO } from 'date-fns'

interface Event {
  id: string
  title: string
  description: string
  location: string | null
  start_date: string
  end_date: string | null
  image_url: string | null
  is_active: number | null
  created_at: string | null
  updated_at: string | null
}

export function EventsManager() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState<'error' | 'success' | ''>('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    image_url: '',
    is_active: true
  })

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('start_date', { ascending: false })

      if (error) throw error
      setEvents(data || [])
    } catch (err) {
      setError('Failed to fetch events')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setStatusMessage('')
    setStatusType('')

    if (!formData.title.trim() || !formData.description.trim() || !formData.start_date) {
      setStatusType('error')
      setStatusMessage('Please fill in all required fields (Title, Description, Start Date).')
      setSaving(false)
      return
    }

    try {
      const eventData: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        location: formData.location.trim() || null,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        image_url: formData.image_url || null,
        is_active: formData.is_active ? 1 : 0,
        updated_at: new Date().toISOString()
      }

      if (editingEvent) {
        // Update existing
        const { error } = await supabase
          .from('events')
          .update(eventData)
          .eq('id', editingEvent.id)

        if (error) throw error
        setStatusType('success')
        setStatusMessage('Event updated successfully.')
      } else {
        // Create new
        eventData.created_at = new Date().toISOString()
        const { error } = await supabase
          .from('events')
          .insert(eventData)

        if (error) throw error
        setStatusType('success')
        setStatusMessage('Event added successfully.')
      }

      await fetchEvents()
      handleCloseDialog()
    } catch (err: any) {
      setStatusType('error')
      setStatusMessage(err.message || 'Failed to save event.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', id)

      if (error) throw error
      setStatusType('success')
      setStatusMessage('Event deleted successfully.')
      await fetchEvents()
    } catch (err: any) {
      setStatusType('error')
      setStatusMessage(err.message || 'Failed to delete event.')
    }
  }

  const handleEdit = (event: Event) => {
    setEditingEvent(event)
    setFormData({
      title: event.title,
      description: event.description,
      location: event.location || '',
      start_date: event.start_date.split('T')[0], // Format for date input
      end_date: event.end_date ? event.end_date.split('T')[0] : '',
      image_url: event.image_url || '',
      is_active: event.is_active === 1
    })
    setIsDialogOpen(true)
  }

  const handleAddNew = () => {
    setEditingEvent(null)
    setFormData({
      title: '',
      description: '',
      location: '',
      start_date: '',
      end_date: '',
      image_url: '',
      is_active: true
    })
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingEvent(null)
    setFormData({
      title: '',
      description: '',
      location: '',
      start_date: '',
      end_date: '',
      image_url: '',
      is_active: true
    })
  }

  const handleToggleActive = async (event: Event) => {
    try {
      const { error } = await supabase
        .from('events')
        .update({
          is_active: event.is_active === 1 ? 0 : 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id)

      if (error) throw error
      await fetchEvents()
    } catch (err: any) {
      setError(err.message || 'Failed to update event status')
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-500 bg-clip-text text-transparent">
            Events Management
          </h2>
          <p className="text-slate-600 text-lg">
            Manage current and past events happening in the village
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={fetchEvents}
            disabled={loading}
            className="w-full sm:w-auto hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-500 hover:from-blue-700 hover:to-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </Button>
        </div>
      </div>

      {statusMessage && (
        <Alert
          className={
            statusType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : statusType === 'error'
                ? undefined
                : undefined
          }
          variant={statusType === 'error' ? 'destructive' : 'default'}
        >
          {statusType === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Events ({events.length})
          </CardTitle>
          <CardDescription>
            Manage events displayed on the Events page
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <RefreshCw className="h-6 w-6 animate-spin mr-3" />
              Loading events...
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-10">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No events added yet.</p>
              <Button onClick={handleAddNew} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Event
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      {event.image_url ? (
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium max-w-xs">
                      <div className="font-medium">{event.title}</div>
                      <div className="text-sm text-muted-foreground line-clamp-1 mt-1">
                        {event.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(parseISO(event.start_date), 'MMM dd, yyyy')}
                        {event.end_date && (
                          <div className="text-muted-foreground">
                            - {format(parseISO(event.end_date), 'MMM dd, yyyy')}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                      {event.location || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={event.is_active === 1}
                          onCheckedChange={() => handleToggleActive(event)}
                        />
                        <Badge variant={event.is_active === 1 ? 'default' : 'secondary'}>
                          {event.is_active === 1 ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(event)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete event?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove this event from the system.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-blue-600 to-purple-500 hover:from-blue-700 hover:to-purple-600"
                                onClick={() => handleDelete(event.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Update event information' : 'Add a new event for the village'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Village Annual Festival 2024"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the event in detail..."
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date (Optional)</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  min={formData.start_date}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location (Optional)</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Village Community Center"
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Event Image (Optional)</Label>
              <ImageUpload
                currentImage={formData.image_url}
                onImageChange={(url) => setFormData({ ...formData, image_url: url })}
                folder="events"
                label="Event Image"
                description="Upload an image for this event"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="is_active">Status</Label>
              <div className="flex items-center gap-2 pt-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className="text-sm text-muted-foreground">
                  {formData.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleCloseDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

